function normalizeBaseUrl(raw: string): string {
  let url = raw.trim();
  url = url.replace(/\/+$/, '');
  url = url.replace(/\/dashboard$/, '');
  return url;
}

// Origin of the Money+ API. Baked in at build time from REACT_APP_API_ORIGIN
// (e.g. a staging host); defaults to production. Keep the default in sync with
// the Flutter app: lib/services/api_service.dart -> <origin>/api/v1
export const API_ORIGIN = (process.env.REACT_APP_API_ORIGIN || 'https://api.moneyplus-pay.com').replace(/\/+$/, '');
const BASE_URL = normalizeBaseUrl(`${API_ORIGIN}/api/v1/admin`);

function getToken(): string | null {
  return localStorage.getItem('moneyplus_token');
}

function headers(): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

async function request(method: string, path: string, body?: any) {
  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers: headers(),
      body: body ? JSON.stringify(body) : undefined,
    });
    const json = await res.json();
    return json;
  } catch (e: any) {
    return { success: false, message: 'Connection error: ' + e.message };
  }
}

/** The API sends null (or a wrapper object) where a list is empty; pages must always get an array. */
export function asList<T = any>(x: unknown): T[] {
  return Array.isArray(x) ? (x as T[]) : [];
}

const api = {
  // Auth
  login: (email: string, password: string) => request('POST', '/login', { email, password }),
  seed: () => request('POST', '/seed'),

  // Dashboard
  dashboard: () => request('GET', '/dashboard'),
  dashboardFinance: () => request('GET', '/dashboard/finance'),
  dashboardCompliance: () => request('GET', '/dashboard/compliance'),
  dashboardSupport: () => request('GET', '/dashboard/support'),

  // Users
  listUsers: (params = '') => request('GET', `/users${params ? '?' + params : ''}`),
  getUser: (id: number) => request('GET', `/users/${id}`),
  createUser: (data: any) => request('POST', '/users', data),
  updateUser: (id: number, data: any) => request('PUT', `/users/${id}`, data),
  deleteUser: (id: number) => request('DELETE', `/users/${id}`),

  // Agents
  listAgents: () => request('GET', '/agents'),
  getAgent: (id: number) => request('GET', `/agents/${id}`),

  // KYC (personal)
  listKYC: (status = '') => request('GET', `/kyc${status ? '?status=' + status : ''}`),
  reviewKYC: (id: number, status: string, reason = '') => request('PUT', `/kyc/${id}`, { status, reason }),

  // KYB (business)
  listKYB: (status = '') => request('GET', `/kyb${status ? '?status=' + status : ''}`),
  reviewKYB: (id: number, status: string, reason = '') => request('PUT', `/kyb/${id}`, { status, reason }),

  // Transactions
  listTransactions: (params = '') => request('GET', `/transactions${params ? '?' + params : ''}`),

  // Merchants (Merchants API + dashboard accounts)
  listMerchants: () => request('GET', '/merchants'),
  createMerchant: (data: any) => request('POST', '/merchants', data),
  fundMerchant: (id: number, amount: number, note = '') => request('POST', `/merchants/${id}/fund`, { amount, note }),
  toggleMerchant: (id: number) => request('PUT', `/merchants/${id}/toggle`),
  rotateMerchantKey: (id: number) => request('POST', `/merchants/${id}/rotate-key`),
  setMerchantPassword: (id: number, password = '') => request('POST', `/merchants/${id}/password`, password ? { password } : {}),

  // Cards
  listCards: () => request('GET', '/cards'),

  // Wallets
  listWallets: () => request('GET', '/wallets'),

  // Splits
  listSplits: () => request('GET', '/splits'),

  // Chat
  listChatThreads: () => request('GET', '/chat/threads'),
  getChatThread: (userId: number) => request('GET', `/chat/${userId}`),
  replyChat: (userId: number, message: string) => request('POST', `/chat/${userId}`, { message }),

  // Help Articles
  listHelpArticles: () => request('GET', '/help-articles'),
  createHelpArticle: (data: any) => request('POST', '/help-articles', data),
  updateHelpArticle: (id: number, data: any) => request('PUT', `/help-articles/${id}`, data),
  deleteHelpArticle: (id: number) => request('DELETE', `/help-articles/${id}`),

  // Admin Accounts
  listAdmins: () => request('GET', '/admins'),
  createAdmin: (data: any) => request('POST', '/admins', data),
  toggleAdmin: (id: number) => request('PUT', `/admins/${id}/toggle`),

  // Audit Logs
  listAuditLogs: (params = '') => request('GET', `/audit-logs${params ? '?' + params : ''}`),

  // Notifications
  listNotifications: () => request('GET', '/notifications'),

  // Crypto Holdings
  listCryptoHoldings: () => request('GET', '/crypto-holdings'),

  // Send rates (per destination) and the default payout fee
  listSendRates: () => request('GET', '/send-rates'),
  updateSendRate: (code: string, data: any) => request('PUT', `/send-rates/${code}`, data),
  resetSendRate: (code: string) => request('DELETE', `/send-rates/${code}`),
  updateRate: (operation: string, data: any) => request('PUT', `/rates/${operation}`, data),

  // Agencies
  listAgencies: (params = '') => request('GET', `/agencies${params ? '?' + params : ''}`),
  getAgency: (id: number) => request('GET', `/agencies/${id}`),
  createAgency: (data: any) => request('POST', '/agencies', data),
  updateAgency: (id: number, data: any) => request('PUT', `/agencies/${id}`, data),
  toggleAgency: (id: number) => request('PUT', `/agencies/${id}/toggle`),
  deleteAgency: (id: number) => request('DELETE', `/agencies/${id}`),

  // Compliance
  listCallbacks: (params = '') => request('GET', `/compliance/callbacks${params ? '?' + params : ''}`),
  listComplianceTransactions: (params = '') => request('GET', `/compliance/transactions${params ? '?' + params : ''}`),
};

export default api;
